import { Store, select, MemoizedSelector, createAction } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { map, filter, distinctUntilChanged } from 'rxjs/operators';
import { denormalize } from 'normalizr';
import isArray from 'lodash-es/isArray';
import keys from 'lodash-es/keys';
import orderBy from 'lodash-es/orderBy';
import isString from 'lodash-es/isString';
import { ZefEntityAction, ZefEntityActionOptions } from './entity-manager.model';
import { ZefWebsocketService } from '../websocket';
import {
  getSubscriptionNameForFeature,
  getFeatureNameWithId,
  addToCacheActionCreator,
  addIdsToCacheActionCreator,
  updateCacheActionCreator,
  removeFromCacheActionCreator,
  removeIdsFromCacheActionCreator,
  getEntityTagKey
} from './utils';
import { EntityOps } from './operations.constant';
import { createTag } from './action-creator.service';
import { selectEntities, selectEntityList, selectEntityListAdditionalInfo, selectSuggests } from './entities.selector';
import { CollectionManagerService } from './collection-manager.service';
import { inject } from '@angular/core';
import { ZefErrorConfig } from '../errors';
import { ZefProgressConfig } from '../progress';
import { zefActionPayload } from '../core';

export class EntityService<E, A = E, U = E> {
  _store = inject(Store);
  _collectionManager = inject(CollectionManagerService);
  _wsService = inject(ZefWebsocketService);

  readonly addOne;
  readonly deleteOne;
  readonly restoreOne;
  readonly updateOne;
  readonly getOne;
  readonly getAll;
  readonly searchAll;
  readonly suggest;
  readonly subscribe;
  readonly updateSubscribe;
  readonly listSubscribe;
  readonly addToCache;
  readonly addIdsToCache;
  readonly updateCache;
  readonly removeFromCache;
  readonly removeIdsFromCache;
  readonly listReset;
  readonly suggestionReset;

  private _schema$: Observable<any>;

  private _entities$ = this._store.pipe(select(selectEntities));

  constructor(
    public readonly entityName: string,
    public readonly significantId?: string
  ) {
    this._schema$ = this._collectionManager.getSchema$(this.entityName);

    this.addOne = createAction(
      createTag(this.entityName, EntityOps.AddOne),
      (
        entity: Partial<A>,
        meta?:  Pick<ZefEntityActionOptions<E>, any> & Partial<ZefEntityActionOptions<E>>,
        errorsConfig?: Partial<ZefErrorConfig>
      ) => this._actionCreator(
        { ...entity },
        EntityOps.AddOne,
        meta,
        errorsConfig
      )
    );

    this.deleteOne = createAction(
      createTag(this.entityName, EntityOps.DeleteOne),
      (
        id: string | number,
        meta?: Partial<ZefEntityActionOptions>,
        errorsConfig?: Partial<ZefErrorConfig>
      ) =>
      this._actionCreator(
        { id },
        EntityOps.DeleteOne,
        meta,
        errorsConfig
      )
    );

    this.restoreOne = createAction(
      createTag(this.entityName, EntityOps.RestoreOne),
      (
        id: string | number,
        meta?: Partial<ZefEntityActionOptions>,
        errorsConfig?: Partial<ZefErrorConfig>
      ) =>
      this._actionCreator(
        { id },
        EntityOps.RestoreOne,
        meta,
        errorsConfig
      )
    );

    this.updateOne = createAction(
      createTag(this.entityName, EntityOps.UpdateOne),
      (
        id: string | number,
        changes: Partial<U>,
        meta?: Partial<ZefEntityActionOptions>,
        errorsConfig?: Partial<ZefErrorConfig>
      ) => this._actionCreator(
        { id, changes },
        EntityOps.UpdateOne,
        meta,
        errorsConfig
      )
    );

    this.getOne = createAction(
      createTag(this.entityName, EntityOps.GetOne),
      (
        id: string | number,
        meta?: Partial<ZefEntityActionOptions>,
        errorsConfig?: Partial<ZefErrorConfig>
      ) =>
      this._actionCreator(
        { id },
        EntityOps.GetOne,
        meta,
        errorsConfig
      )
    );

    this.getAll = createAction(
      createTag(this.entityName, EntityOps.GetAll),
      (
        data?: any,
        meta?: Partial<ZefEntityActionOptions>,
        errorsConfig?: Partial<ZefErrorConfig>
      ) => this._actionCreator(
        data,
        EntityOps.GetAll,
        meta,
        errorsConfig
      )
    );

    this.searchAll = createAction(
      createTag(this.entityName, EntityOps.SearchAll),
      (
        data?: any,
        meta?: Partial<ZefEntityActionOptions>,
        errorsConfig?: Partial<ZefErrorConfig>
      ) => this._actionCreator(
        data,
        EntityOps.SearchAll,
        meta,
        errorsConfig
      )
    );

    this.suggest = createAction(
      createTag(this.entityName, EntityOps.Suggest),
      (
        id: string | { clientId: string; name: string; },
        data?: any,
        meta?: Partial<ZefEntityActionOptions>,
        errorsConfig?: Partial<ZefErrorConfig>
      ) => this._actionCreator(
        {
          ...data,
          search: data && data.search
            ? [
              ...data.search,
              this._getClientIdSearchObject(id)
            ].filter((d) => d !== undefined)
            : [ this._getClientIdSearchObject(id) ],
        },
        EntityOps.Suggest,
        meta,
        errorsConfig
      )
    );

    this.subscribe = createAction(
      createTag(this.entityName, EntityOps.Subscribe),
      (
        subscriptionName: string,
        data?: any,
        meta?: Partial<ZefEntityActionOptions>,
        errorsConfig?: Partial<ZefErrorConfig>
      ) => this._actionCreator(
        {
          ...data,
          subscriptionName,
          disableOutput: true,
          wsOutputType: 'updateStream'
        },
        EntityOps.Subscribe,
        meta,
        errorsConfig
      )
    );

    this.updateSubscribe = createAction(
      createTag(this.entityName, EntityOps.UpdateSubscribe),
      (
        clientId: string | { clientId: string; name: string; },
        data?: any,
        meta?: Partial<ZefEntityActionOptions>,
        errorsConfig?: Partial<ZefErrorConfig>
      ) => this._actionCreator(
        {
          ...data,
          search: data && data.search
            ? [
              ...data.search,
              this._getClientIdSearchObject(clientId)
            ].filter((d) => d !== undefined)
            : [ this._getClientIdSearchObject(clientId) ],
          subscriptionName: getSubscriptionNameForFeature(this.entityName, 'update'),
          disableOutput: true,
          wsOutputType: 'updateStream',
          receiverId: this._wsService?.getReceiverId(),
        },
        EntityOps.UpdateSubscribe,
        meta,
        errorsConfig
      )
    );

    this.listSubscribe = createAction(
      createTag(this.entityName, EntityOps.ListSubscribe),
      (
        clientId: string | { clientId: string; name: string; },
        featureName: string | { name: string; id: string; } = '',
        data: any = {},
        meta?: Partial<ZefEntityActionOptions>,
        errorsConfig?: Partial<ZefErrorConfig>,
        progressConfig?: Partial<ZefProgressConfig>
      ) => this._actionCreator(
        {
          ...data,
          search: data.search
            ? [
              ...data.search,
              this._getClientIdSearchObject(clientId)
            ].filter((d) => d !== undefined)
            : [ this._getClientIdSearchObject(clientId) ],
          subscriptionName: getSubscriptionNameForFeature(
            this.entityName,
            'list',
            meta && meta.handleGlobally === false
              ? undefined
              : isString(featureName)
                ? featureName
                : featureName.name
          ),
          receiverId: this._wsService?.getReceiverId(),
          wsOutputType: 'listStream'
        },
        EntityOps.ListSubscribe,
        meta
          ? meta.handleGlobally !== false
            ? {
              tag: getFeatureNameWithId(featureName),
              handleGlobally: true,
              ...meta
            } : undefined
          : {
            tag: getFeatureNameWithId(featureName),
            handleGlobally: true
          },
        errorsConfig,
        progressConfig
      )
    );

    this.addToCache = addToCacheActionCreator<E>(this.entityName);
    this.addIdsToCache = addIdsToCacheActionCreator(this.entityName);
    this.updateCache = updateCacheActionCreator<E>(this.entityName);
    this.removeFromCache = removeFromCacheActionCreator(this.entityName);
    this.removeIdsFromCache = removeIdsFromCacheActionCreator(this.entityName);

    // addIdsToCache = createAction(
    //   createTag(this.entityName, EntityOps.AddIdsToCache),
    //   (data: string[], meta?: Partial<ZefEntityActionOptions>, totalHits?: number) => ({
    //     data,
    //     meta,
    //     op: EntityOps.AddIdsToCache,
    //     entityName: this.entityName,
    //     totalHits
    //   })
    // );

    // updateCache = createAction(
    //   createTag(this.entityName, EntityOps.UpdateCache),
    //   (data: Partial<E>[], meta?: Partial<ZefEntityActionOptions>) => ({
    //     data,
    //     meta,
    //     op: EntityOps.UpdateCache,
    //     entityName: this.entityName
    //   })
    // );

    // removeFromCache = createAction(
    //   createTag(this.entityName, EntityOps.RemoveFromCache),
    //   // _ = meta
    //   (id: string, _?: Partial<ZefEntityActionOptions>) => ({
    //     data: { id },
    //     op: EntityOps.RemoveFromCache,
    //     entityName: this.entityName
    //   })
    // );

    // removeIdsFromCache = createAction(
    //   createTag(this.entityName, EntityOps.RemoveIdsFromCache),
    //   (data: string[], meta?: Partial<ZefEntityActionOptions>, totalHits?: number) => ({
    //     data,
    //     meta,
    //     op: EntityOps.RemoveIdsFromCache,
    //     entityName: this.entityName,
    //     totalHits
    //   })
    // );

    this.listReset = createAction(
      createTag(this.entityName, EntityOps.ListReset),
      (tag?: string | { name: string; id: string; }) => ({
        tag,
        op: EntityOps.ListReset,
        entityName: this.entityName
      })
    );

    this.suggestionReset = createAction(
      createTag(this.entityName, EntityOps.SuggestReset),
      (tag?: string | { name: string; id: string; }) => ({
        tag,
        op: EntityOps.SuggestReset,
        entityName: this.entityName
      })
    );
  }

  list$(
    tag?: string | { name: string; id: string; },
    orderSelector?: Array<string | ((i: E) => any)>,
    orderDir?: Array<boolean | 'asc' | 'desc'>
  ) {
    return combineLatest([
      this._store.pipe(select(selectEntityList(this.entityName, getFeatureNameWithId(tag)))),
      this._entities$,
      this._schema$
    ]).pipe(
      map(([ ids, entities, entSchema ]) => {

        if ((!ids || !entities)) { return undefined; }

        const d = this._denormalize(ids, entities, entSchema);

        if (orderSelector) {
          return orderBy(d, orderSelector, orderDir);
        } else {
          return d;
        }

      }),
      distinctUntilChanged()
    );
  }

  listAdditionalInfo$(tag?: string | { name: string; id: string; }) {
    return this._store.pipe(
      select(selectEntityListAdditionalInfo(this.entityName, getFeatureNameWithId(tag)),
      distinctUntilChanged()
    ));
  }

  entityById$(
    selectorOrId: MemoizedSelector<any, string> | string | number,
    distinctFn = (a: any, b: any) => a && b && (a as any).id !== (b as any).id
  ) {
    const baseStreams$ = [
      this._entities$,
      this._schema$
    ];

    const streams$ = typeof selectorOrId === 'string' || typeof selectorOrId === 'number'
      ? baseStreams$
      : [
        ...baseStreams$,
        this._store.pipe(select(selectorOrId)),
      ];

    return combineLatest(streams$).pipe(
      map(([ entities, entSchema, id ]) => {
        // filter not used on purpose.. TODO better comment later
        if (!entities || !entities[this.entityName]) { return undefined; }
        if (!id) {
          return this._denormalize([ selectorOrId as any ], entities, entSchema)[0];
        }
        return this._denormalize([ id ], entities, entSchema)[0];
      }),
      distinctUntilChanged(distinctFn)
    ) as Observable<E>;
  }

  listByIds$(selector: MemoizedSelector<any, string[]>) {
    return combineLatest([
      this._store.pipe(select(selector)),
      this._entities$,
      this._schema$
    ]).pipe(
      filter(([ ids, entities ]) => !!(ids && entities && entities[this.entityName])),
      map(([ ids, entities, entSchema ]) => this._denormalize(ids, entities, entSchema)),
      distinctUntilChanged()
    );
  }

  all$() {
    return combineLatest([
      this._entities$,
      this._schema$
    ])
    .pipe(
      map(([ entities, entSchema ]) => {
        const ent = entities[this.entityName];
        const ids = keys(ent).reduce((arr, id) => {
          arr.push(ent[id].id);
          return arr;
        }, []);
        return this._denormalize(ids, entities, entSchema);
      }),
      distinctUntilChanged()
    );
  }

  suggests$(
    tag?: string
  ) {
    return this._store.pipe(
      select(selectSuggests),
      map((d) => d[getEntityTagKey(this.entityName, tag)]),
      distinctUntilChanged()
    );
  }

  rawEntities$() {
    return this._store.pipe(
      select(selectEntities),
      map((entities) => entities[this.entityName] as { [id: string]: E; }),
      distinctUntilChanged()
    );
  }

  private _getClientIdSearchObject(clientId: string | { clientId: string; name: string; }) {
    if (!clientId) { return undefined; }
    if (isString(clientId)) {
      return {
        name: this.significantId ? this.significantId : 'clientId',
        operator: 'eq',
        value: clientId
      };
    } else {
      return {
        name: clientId.name,
        operator: 'eq',
        value: clientId.clientId
      };
    }
  }

  private _denormalize(ids: string[] | string | number | number[], entities: any, entSchema: any) {
    const arrIds = isArray(ids) ? ids : [ ids ];

    if (entSchema) {
      return denormalize(
        arrIds,
        entSchema.list,
        entities
      ).filter((item: E) => !!item) as E[];
    }

    return undefined;
  }

  private _actionCreator<D>(
    data: D,
    op: EntityOps,
    meta = {},
    errorsConfig: Partial<ZefErrorConfig> = {},
    progressConfig: Partial<ZefProgressConfig> = {}
  ): Partial<ZefEntityAction<D>> {
    return {
      ...zefActionPayload(
        { data, meta },
        {
          type: 'snack',
          ...errorsConfig,
        },
        { ...progressConfig }
      ),
      op,
      entityName: this.entityName
    };

  }
}
